import {
    Box,
    Button,
    Card,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    styled,
    TextField,
} from "@mui/material";
import { ChangeEvent, KeyboardEvent, useEffect } from "react";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import MultiSelectList from "../../../components/multi-select-list/multi-select-list";
import { IListItem } from "../../../interface/list-item";
import { IRootState } from "../../../redux/root.reducer";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";
import { FormParams } from "../enums/form-params";
import { Steps } from "../enums/steps";
import { IError, IFiltersForm } from "../interface/form";

const defaultOption = [{ id: "", name: "" }];

const unitedStatesId = "US";

const selector = createSelector(
    (state: IRootState) => state.countries,
    (state: IRootState) => state.states,
    (state: IRootState) => state.listBuilder.listBuilder.data,
    (countries, states, listBuilder) => ({
        minLocationCount: listBuilder?.minLocationCount,
        maxLocationCount: listBuilder?.maxLocationCount,
        excludeDistributors: !!listBuilder?.excludeDistributors,
        countries: {
            ...countries,
            selected: countries.data?.filter(({ id }) =>
                listBuilder?.countryFilter.includes(id),
            ),
        },
        states: {
            options: (states.data ?? []).map((code) => ({
                id: code,
                name: code,
            })),
            selected: states.data
                ?.filter((code) => listBuilder?.stateFilter.includes(code))
                .map((code) => ({
                    id: code,
                    name: code,
                })),
            isLoading: states.isLoading,
        },
    }),
);

interface IProps extends IWithClassName {
    error: IError;
    onSelectChange: (
        step: Steps,
        label: FormParams,
    ) => (options: Array<IListItem>) => void;
    form: IFiltersForm;
    onCheckBoxChange: (
        step: Steps,
        label: FormParams,
    ) => (event: ChangeEvent<HTMLInputElement>) => void;
    initStepForm: (step: Steps, form: IFiltersForm) => void;
    onNumberChange: (
        step: Steps,
        label: FormParams,
    ) => (event: ChangeEvent<HTMLInputElement>) => void;
}

const Filters = ({
    className,
    onSelectChange,
    onNumberChange,
    form,
    error,
    onCheckBoxChange,
    initStepForm,
}: IProps) => {
    const {
        countries,
        states,
        excludeDistributors,
        maxLocationCount,
        minLocationCount,
    } = useSelector(selector);

    const checkUSSelected = (selectedCountriesList: Array<IListItem>) =>
        !!selectedCountriesList.filter(({ id }) => id === unitedStatesId)
            .length;

    const handleCountryChange = (countryList: Array<IListItem>) => {
        const sortedCountryList = [...countryList].sort((a, b) =>
            a.name.localeCompare(b.name),
        );
        onSelectChange(
            Steps.FILTERS,
            FormParams.COUNTRY_FILTER,
        )(sortedCountryList);

        if (!checkUSSelected(sortedCountryList) && form?.stateFilter) {
            onSelectChange(Steps.FILTERS, FormParams.STATE_FILTER)([]);
        }
    };

    useEffect(() => {
        initStepForm(Steps.FILTERS, {
            [FormParams.COUNTRY_FILTER]: countries.selected,
            [FormParams.STATE_FILTER]: states.selected,
            [FormParams.MIN_LOCATION_COUNT]: minLocationCount,
            [FormParams.MAX_LOCATION_COUNT]: maxLocationCount,
            [FormParams.EXCLUDE_DISTRIBUTORS]: excludeDistributors,
        });
    }, [
        countries,
        states,
        excludeDistributors,
        maxLocationCount,
        minLocationCount,
    ]);

    const clearAll = (label: FormParams) =>
        onSelectChange(Steps.FILTERS, label)([]);

    const selectAll = (label: FormParams) => {
        onSelectChange(
            Steps.FILTERS,
            label,
        )(
            label === FormParams.COUNTRY_FILTER
                ? (countries.data ?? []).sort((a, b) =>
                      a.name.localeCompare(b.name),
                  )
                : (states.options ?? []),
        );
    };

    const handleLocationCountInput = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        const sanitizedValue = value.replace(/\D/g, "");
        event.target.value = sanitizedValue;
    };

    const handleLocationCountKeyDown = (
        event: KeyboardEvent<HTMLInputElement>,
    ) => {
        if (["e", "E", "+", "-", "."].includes(event.key)) {
            event.preventDefault();
        }
    };

    return (
        <Box className={className}>
            <Card className="code-form">
                <Box className="code-form-row-1">
                    <FormControl className="select-input">
                        <FormLabel className="text-center">
                            Country Filter:
                        </FormLabel>
                        <MultiSelectList
                            options={countries.data ?? defaultOption}
                            onChange={handleCountryChange}
                            selectedItems={form?.countryFilter ?? []}
                            multiline={true}
                            menuWidth={300}
                        />
                        <Button
                            variant="outlined"
                            color="secondary"
                            className="text-center button-style"
                            onClick={() => handleCountryChange([])}
                        >
                            CLEAR
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            className="text-center button-style"
                            onClick={() => selectAll(FormParams.COUNTRY_FILTER)}
                        >
                            ADD ALL
                        </Button>
                    </FormControl>
                    <FormControl
                        className="select-input"
                        disabled={!checkUSSelected(form?.countryFilter ?? [])}
                    >
                        <FormLabel className="text-center">
                            State Filter:
                        </FormLabel>
                        <MultiSelectList
                            options={states.options ?? defaultOption}
                            onChange={onSelectChange(
                                Steps.FILTERS,
                                FormParams.STATE_FILTER,
                            )}
                            selectedItems={form?.stateFilter ?? []}
                            multiline={true}
                            menuWidth={200}
                            disable={
                                !checkUSSelected(form?.countryFilter ?? [])
                            }
                        />

                        <Button
                            variant="outlined"
                            color="secondary"
                            className="text-center button-style"
                            onClick={() => clearAll(FormParams.STATE_FILTER)}
                            disabled={
                                !checkUSSelected(form?.countryFilter ?? [])
                            }
                        >
                            CLEAR
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            className="text-center button-style"
                            onClick={() => selectAll(FormParams.STATE_FILTER)}
                            disabled={
                                !checkUSSelected(form?.countryFilter ?? [])
                            }
                        >
                            ADD ALL
                        </Button>
                    </FormControl>
                </Box>

                <Box className="code-form-row-1">
                    <FormControl>
                        <FormLabel className="text-center">
                            Minimum Location Count:
                        </FormLabel>
                        <TextField
                            type="number"
                            variant="standard"
                            className="number-box"
                            value={form[FormParams.MIN_LOCATION_COUNT] ?? ""}
                            error={!!error[FormParams.MIN_LOCATION_COUNT]}
                            helperText={
                                error[FormParams.MIN_LOCATION_COUNT] ?? ""
                            }
                            onChange={onNumberChange(
                                Steps.FILTERS,
                                FormParams.MIN_LOCATION_COUNT,
                            )}
                            onInput={handleLocationCountInput}
                            onKeyDown={handleLocationCountKeyDown}
                            InputProps={{ inputProps: { min: 0 } }}
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel className="text-center">
                            Maximum Location Count:
                        </FormLabel>
                        <TextField
                            type="number"
                            className="number-box"
                            variant="standard"
                            value={form[FormParams.MAX_LOCATION_COUNT] ?? ""}
                            onChange={onNumberChange(
                                Steps.FILTERS,
                                FormParams.MAX_LOCATION_COUNT,
                            )}
                            onInput={handleLocationCountInput}
                            onKeyDown={handleLocationCountKeyDown}
                            InputProps={{ inputProps: { min: 0 } }}
                        />
                    </FormControl>
                    <FormControlLabel
                        control={
                            <Checkbox
                                onChange={onCheckBoxChange(
                                    Steps.FILTERS,
                                    FormParams.EXCLUDE_DISTRIBUTORS,
                                )}
                                checked={
                                    form[FormParams.EXCLUDE_DISTRIBUTORS] ??
                                    false
                                }
                            />
                        }
                        label="Exclude Distributors"
                    />
                </Box>
            </Card>
        </Box>
    );
};

export default styled(Filters)(({ theme }: IWithThemeProps) => ({
    display: "grid",
    gap: 20,
    ".code-form": {
        padding: theme?.spacing(2),
        ".code-form-row-1": {
            display: "grid",
            gridTemplateColumns: "1fr 1fr auto",
            gap: 10,
        },
        ".MuiFormControl-root": {
            flexDirection: "row",
            "&.number-box": {
                flexDirection: "column",
            },
        },
        ".code-form-row-2": {
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 10,
        },
        ".search-box": {
            backgroundColor: theme?.palette.collapsibleFeatures.bgColor,
        },
        ".select-input .MuiBox-root": {
            width: "100%",
            margin: theme?.spacing("auto", 1),
        },
        "span:has(.skeleton)": {
            width: "100%",
            margin: theme?.spacing("auto", 1),
        },
        ".MuiTextField-root": {
            width: "100%",
            ".MuiInput-root": {
                width: "inherit",
            },
        },
        ".text-center": {
            whiteSpace: "nowrap",
        },
        ".button-style": {
            fontSize: 15,
            height: 30,
            margin: theme?.spacing("auto", 0.25),
        },
    },
    ".MuiChip-label": {
        maxWidth: 150,
    },
    ".number-box": {
        margin: theme?.spacing(0, 1),
    },
}));
